import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { safeFormat } from "@/lib/dateUtils";
import { Bell, CheckCheck, Info, CheckCircle2, AlertTriangle, XCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const TYPE_ICON = {
  info: <Info className="w-4 h-4 text-blue-500 shrink-0" />,
  success: <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />,
  warning: <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />,
  error: <XCircle className="w-4 h-4 text-red-500 shrink-0" />,
};

const TYPE_BG = {
  info: "bg-blue-500/10 border-blue-500/20",
  success: "bg-green-500/10 border-green-500/20",
  warning: "bg-amber-500/10 border-amber-500/20",
  error: "bg-red-500/10 border-red-500/20",
};

export function SalonNotificationBell() {
  const [open, setOpen] = useState(false);
  const utils = trpc.useUtils();

  const { data: notifications } = trpc.salon.getNotifications.useQuery(undefined, {
    refetchInterval: 30000, // Atualiza a cada 30s
  });
  const { data: unreadCount } = trpc.salon.getUnreadCount.useQuery(undefined, {
    refetchInterval: 30000,
  });

  const markRead = trpc.salon.markNotificationsRead.useMutation({
    onSuccess: () => {
      utils.salon.getUnreadCount.invalidate();
      utils.salon.getNotifications.invalidate();
    },
  });

  const handleOpen = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen && unreadCount && unreadCount > 0) {
      markRead.mutate();
    }
  };

  const count = unreadCount ?? 0;

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9">
          <Bell className="w-4 h-4" />
          {count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center font-bold">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/50">
          <span className="font-medium text-sm">Notificações</span>
          {count === 0 && (
            <Badge variant="secondary" className="text-xs">Tudo lido</Badge>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {!notifications || notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center px-4">
              <Bell className="w-8 h-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Nenhuma notificação ainda.</p>
            </div>
          ) : (
            <div className="divide-y divide-border/30">
              {notifications.map((notif: any) => (
                <div
                  key={notif.id}
                  className={`p-3 ${!notif.isRead ? "bg-primary/5" : ""}`}
                >
                  <div className={`rounded-lg border p-3 space-y-1 ${TYPE_BG[notif.type as keyof typeof TYPE_BG] ?? TYPE_BG.info}`}>
                    <div className="flex items-start gap-2">
                      {TYPE_ICON[notif.type as keyof typeof TYPE_ICON] ?? TYPE_ICON.info}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold leading-tight">{notif.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{notif.message}</p>
                        {/* Exibir telefone de contato se presente na mensagem */}
                        {notif.message?.includes("Contato:") && (
                          <a
                            href={`https://wa.me/55${(notif.message.match(/Contato: ([0-9() -]+)/)?.[1] ?? "").replace(/\D/g, "")}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 mt-1.5 text-xs text-green-600 hover:underline"
                          >
                            <Phone className="w-3 h-3" />
                            Falar no WhatsApp
                          </a>
                        )}
                        <p className="text-[10px] text-muted-foreground/60 mt-1">
                          {safeFormat(notif.createdAt, "dd/MM/yyyy HH:mm")}
                        </p>
                      </div>
                      {!notif.isRead && (
                        <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-0.5" />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {notifications && notifications.length > 0 && (
          <div className="border-t border-border/50 px-4 py-2">
            <p className="text-xs text-muted-foreground text-center">
              {notifications.length} notificação{notifications.length !== 1 ? "ões" : ""}
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
